/**
 * AgreementDetailsStep - Collect service details for the agreement
 *
 * This step is shown for AI and manual creation types.
 * For template type, some fields may be pre-filled.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FlowInput, FlowTextarea, FlowSelect, FlowNavigation, StepContainer, InfoBox } from '../FlowComponents';

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'CAD', label: 'CAD ($)' },
  { value: 'AUD', label: 'AUD ($)' },
];

const SERVICE_PRESETS = [
  { label: 'Wedding Photography', scope: 'Full-day wedding photography coverage including ceremony, reception, and portraits' },
  { label: 'Portrait Session', scope: 'Professional portrait photography session with edited digital images' },
  { label: 'Event Coverage', scope: 'Event photography coverage with edited digital images' },
  { label: 'Commercial Shoot', scope: 'Commercial photography for business marketing and branding' },
  { label: 'Custom', scope: '' },
];

const AgreementDetailsStep = ({ data, updateData, goNext, goBack }) => {
  const [showPresets, setShowPresets] = useState(false);

  const handleChange = (field, value) => {
    updateData({ [field]: value });
  };

  const handlePresetSelect = (preset) => {
    if (preset.scope) {
      updateData({ scope: preset.scope });
    }
    setShowPresets(false);
  };

  // Allow continuing even with partial data - better UX
  const canContinue = true;

  return (
    <StepContainer>
      <div className="max-w-xl mx-auto space-y-6">
        {/* Quick tip for AI mode */}
        {data.agreementType === 'ai' && (
          <InfoBox
            title="Tip: Be Descriptive"
            description="The more details you provide about the scope of work, the better your AI-generated agreement will be."
            variant="tip"
          />
        )}

        {/* Scope of Work */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Scope of Work
            </label>
            <button
              onClick={() => setShowPresets(!showPresets)}
              className="text-xs text-brand hover:text-[#FFA164] transition-colors"
            >
              {showPresets ? 'Hide presets' : 'Use preset'}
            </button>
          </div>

          {showPresets && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex flex-wrap gap-2 pb-2"
            >
              {SERVICE_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => handlePresetSelect(preset)}
                  className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#2A2A2A] text-gray-700 dark:text-[#999999] rounded-lg transition-colors"
                >
                  {preset.label}
                </button>
              ))}
            </motion.div>
          )}

          <FlowTextarea
            placeholder="Describe the services you're providing..."
            value={data.scope || ''}
            onChange={(e) => handleChange('scope', e.target.value)}
            rows={4}
            hint={data.agreementType === 'ai' ? 'AI will use this to generate detailed terms' : null}
          />
        </div>

        {/* Price and Currency */}
        <div className="grid grid-cols-2 gap-4">
          <FlowInput
            label="Total Price"
            type="number"
            placeholder="0.00"
            value={data.price || ''}
            onChange={(e) => handleChange('price', e.target.value)}
          />
          <FlowSelect
            label="Currency"
            options={CURRENCY_OPTIONS}
            value={data.currency || 'USD'}
            onChange={(e) => handleChange('currency', e.target.value)}
          />
        </div>

        {/* Timeline / Due Date */}
        <FlowInput
          label="Project Date / Deadline"
          type="date"
          value={data.timeline || ''}
          onChange={(e) => handleChange('timeline', e.target.value)}
          hint="When is the service being delivered?"
        />

        {/* Event Details (optional, for photography agreements) */}
        <div className="pt-4 border-t border-gray-200 dark:border-[#1A1A1A]">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
            Event Details (Optional)
          </h3>

          <div className="space-y-4">
            <FlowInput
              label="Venue / Location"
              placeholder="e.g., Grand Ballroom, Downtown Hotel"
              value={data.venue || ''}
              onChange={(e) => handleChange('venue', e.target.value)}
            />

            <div className="grid grid-cols-2 gap-4">
              <FlowInput
                label="Start Time"
                type="time"
                value={data.startTime || ''}
                onChange={(e) => handleChange('startTime', e.target.value)}
              />
              <FlowInput
                label="Duration"
                placeholder="e.g., 8 hours"
                value={data.duration || ''}
                onChange={(e) => handleChange('duration', e.target.value)}
              />
            </div>

            <FlowTextarea
              label="Special Requirements"
              placeholder="Any special requests or requirements..."
              value={data.specialRequirements || ''}
              onChange={(e) => handleChange('specialRequirements', e.target.value)}
              rows={2}
            />
          </div>
        </div>

        {/* Navigation */}
        <div className="pt-4">
          <FlowNavigation
            onBack={goBack}
            onContinue={goNext}
            disabled={!canContinue}
            continueLabel="Continue"
          />
        </div>
      </div>
    </StepContainer>
  );
};

export default AgreementDetailsStep;
